import isElectron from 'is-electron';
import { WorkspaceWindowTypeEnumType } from '@mark43/cad-api';
import { truncate } from 'lodash';
import { AppName } from '../../app-name/app-name';
import {
    TrackingType,
    TrackingMetadata,
    AdditionalMetadata,
    UserActionTrackerInstance,
} from './types';
import { getEventActionSource } from './utils';

const UNKNOWN = 'Unknown';

type TrackingGlobalParams = {
    appName: AppName;
    appVersion?: string;
    rendererVersion: string;
    trackType?: keyof typeof TrackingType;
    event?: React.MouseEvent | React.KeyboardEvent;
    workspaceWindowTypeRef: React.MutableRefObject<WorkspaceWindowTypeEnumType>;
};

type UserActionTrackerParams = TrackingGlobalParams & {
    trackingMetadata: TrackingMetadata;
};

//From pendo: 'As a general guideline, 20 properties can cause performance issues if the strings are large, and 50 properties are likely to cause performance issues. Strings should be less than 500 bytes.'
const processTrackingMetadataForPendo = ({
    trackingMetadata,
    appName,
    appVersion,
    rendererVersion,
    windowType,
    event,
    isElectron,
}: {
    trackingMetadata: TrackingMetadata;
    appName: AppName;
    rendererVersion: string;
    appVersion?: string;
    windowType?: WorkspaceWindowTypeEnumType;
    event?: React.MouseEvent | React.KeyboardEvent;
    isElectron?: boolean;
}): pendo.Metadata => {
    const { trackType, feature, component, actionSource, trackingData } = trackingMetadata ?? {};

    let additionalMetadata: AdditionalMetadata = {
        feature: feature ?? UNKNOWN,
        component: component ?? UNKNOWN,
        actionSource: actionSource || '',
    };
    if (
        trackType === 'CLI' &&
        ('commandSegment' in trackingMetadata ||
            'command' in trackingMetadata ||
            'executionView' in trackingMetadata)
    ) {
        additionalMetadata = {
            feature: feature ?? 'CLI',
            component: component ?? 'CLI',
            actionSource: '-',

            argumentDefinition:
                trackingMetadata.commandSegment?.segmentData?.argumentDefinition ?? UNKNOWN,
            cliArgument: trackingMetadata.commandSegment?.segmentData?.cliArgument ?? UNKNOWN,
            title: truncate(trackingMetadata.commandSegment?.segmentData?.title ?? UNKNOWN),
            commandText: truncate(trackingMetadata.command?.commandText ?? UNKNOWN),
            shouldRepeat: !!trackingMetadata.commandSegment?.segmentData?.shouldRepeat,
            eventCount: trackingMetadata.executionView?.agencyEventIds?.length ?? -1,
            unitCount: trackingMetadata.executionView?.potentialUnitCallSigns?.length ?? -1,
            error: truncate(trackingMetadata.errorMessage, { length: 50 }),
            statusCode: trackingMetadata.statusCode ?? '',
            validator: trackingMetadata.validator ?? '',
        };
    }
    if (trackType === 'CLI_STASH' && 'lastStashed' in trackingMetadata) {
        additionalMetadata = {
            feature: feature ?? 'Stash Command',
            component: component ?? 'CLI',
            actionSource: actionSource ?? getEventActionSource(event),

            stashedCount: trackingMetadata.stashedCount ?? 0,
            lastStashed: truncate(trackingMetadata.lastStashed),
            stashTrigger: trackingMetadata.stashTrigger ?? UNKNOWN,
        };
    }
    if (trackType === 'KEYBOARD_SHORTCUT' && 'shortcutKeyView' in trackingMetadata) {
        additionalMetadata = {
            feature: feature ?? 'Shortcut',
            component: component ?? 'Shortcut',
            actionSource: '-',

            shortcutCommand: trackingMetadata.shortcutKeyView?.shortcutCommand ?? UNKNOWN,
            shortcutContext: truncate(trackingMetadata.shortcutKeyView?.shortcutContext ?? UNKNOWN),
            shortcutKeyCombination:
                trackingMetadata.shortcutKeyView?.shortcutKeyCombination ?? UNKNOWN,
        };
    }
    if (trackType === 'MAP_SHORTCUT' && 'mapShortcutKey' in trackingMetadata) {
        additionalMetadata = {
            feature: feature ?? 'Map Shortcut',
            component: component ?? 'Map',
            actionSource: '-',

            mapShortcutKey: trackingMetadata?.mapShortcutKey ?? UNKNOWN,
            mapShortcutText: truncate(trackingMetadata?.mapShortcutText ?? UNKNOWN),
        };
    }
    if (trackType === 'NEW_EVENT' && 'newEventTrigger' in trackingMetadata) {
        additionalMetadata = {
            feature: feature ?? 'New Event',
            component: component ?? 'Event',
            actionSource: getEventActionSource(event),

            trigger: trackingMetadata?.newEventTrigger ?? UNKNOWN,
        };
    }
    const metadata: pendo.Metadata = {
        ...trackingData,
        ...additionalMetadata,
        app: appName,
        actionSource: additionalMetadata.actionSource
            ? additionalMetadata.actionSource
            : getEventActionSource(event),
        appVersion: appVersion ?? '',
        rendererVersion,
        windowType: windowType ?? UNKNOWN,
        isElectron: isElectron ?? UNKNOWN,
    };
    return metadata;
};

const triggerPendoTracking = (
    pendo: typeof window.pendo,
    {
        appName,
        appVersion,
        rendererVersion,
        trackingMetadata,
        trackType,
        event,
        workspaceWindowTypeRef,
    }: UserActionTrackerParams
) => {
    if (pendo?.isReady?.()) {
        pendo.track?.(
            trackType ?? trackingMetadata.trackType ?? '',
            processTrackingMetadataForPendo({
                trackingMetadata: {
                    ...trackingMetadata,
                    trackType: trackType ?? trackingMetadata.trackType ?? '',
                },
                appName,
                appVersion,
                rendererVersion,
                // for cad, each window has its own pendo instance, and window type will not change.
                // for fr, there is only one windown, no need to track the window change, use the initial window type for entire tracking session (the initial page of first responder)
                windowType: workspaceWindowTypeRef.current,
                event,
                isElectron: isElectron(),
            })
        );
    }
};


export const createPendoTrackingInstance = (
    pendo: typeof window.pendo,
    trackingGlobalParams: TrackingGlobalParams
): UserActionTrackerInstance => {
    return {
        track: (trackingMetadata, event) => {
            triggerPendoTracking(pendo, {
                ...trackingGlobalParams,
                event,
                trackingMetadata,
                trackType: trackingMetadata.trackType,
            });
        },
        trackCommandLine: (trackingCommandLineMetadata) => {
            triggerPendoTracking(pendo, {
                ...trackingGlobalParams,
                trackingMetadata: trackingCommandLineMetadata,
                trackType: 'CLI',
            });
        },
        trackStashCommand: (trackingStashCommandMetadata, event) => {
            triggerPendoTracking(pendo, {
                ...trackingGlobalParams,
                event,
                trackingMetadata: trackingStashCommandMetadata,
                trackType: 'CLI_STASH',
            });
        },
        trackShortcut: (trackingShortcutMetadata) => {
            triggerPendoTracking(pendo, {
                ...trackingGlobalParams,
                trackingMetadata: trackingShortcutMetadata,
                trackType: 'KEYBOARD_SHORTCUT',
            });
        },
        trackMapShortcut: (trackingMapShortcutMetadata) => {
            triggerPendoTracking(pendo, {
                ...trackingGlobalParams,
                trackingMetadata: trackingMapShortcutMetadata,
                trackType: 'MAP_SHORTCUT',
            });
        },
        trackNewEvent: (trackingNewEventMetadata, event) => {
            triggerPendoTracking(pendo, {
                ...trackingGlobalParams,
                event,
                trackingMetadata: trackingNewEventMetadata,
                trackType: 'NEW_EVENT',
            });
        },
    };
};
